import { v4 as uuidv4 } from "uuid";

/**
 * Advanced Filters Helper Functions
 */

/**
 * Creates an empty filter object with default values
 * @returns {Object} Empty filter object
 */
export const createEmptyFilter = () => ({
  id: uuidv4(),
  category: "",
  property: "",
  operator: "",
  value: "",
  valueEnd: "", // For range inputs (between operator, numeric_range, date_range)
  appliedValues: [],
});

/**
 * Checks if a filter is empty (in default state)
 * @param {Object} filter - Filter object to check
 * @returns {boolean} True if filter is empty
 */
export const isFilterEmpty = (filter) =>
  !filter.category &&
  !filter.property &&
  !filter.operator &&
  !filter.value &&
  !filter.valueEnd &&
  (!filter.appliedValues || filter.appliedValues.length === 0);

/**
 * Checks if filters array is in default state (single empty filter with AND logic)
 * @param {Array} filters - Array of filter objects
 * @param {string} logicOperator - Current logic operator
 * @returns {boolean} True if filters are in default state
 */
export const areFiltersInDefaultState = (filters, logicOperator) =>
  filters.length === 1 && isFilterEmpty(filters[0]) && logicOperator === "AND";

/**
 * Operators that don't require a value input.
 *
 * BK-2907 cleanup: dropped 'has'/'does_not_have' (existence dataType removed).
 */
export const NO_VALUE_OPERATORS = [
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
  "today",
  "this_week",
  "this_month",
  "this_quarter",
  "this_year",
];

// Per BK-2907 canonical contract:
//   Shape A (single value):  populates `value` (and `valueEnd` for ranges/units),
//                            `appliedValues` is empty
//   Shape B (multi value):   populates `appliedValues: [{value, label}, ...]`,
//                            `value`/`valueEnd` are empty
const SHAPE_B_DATA_TYPES = new Set(["text", "list", "list_multi"]);

/**
 * Resolves the dataType for a filter from the category config or an API-ready
 * saved filter. Falls back to 'text' for unknown UI filters.
 */
const resolveDataType = (filter, filterCategories) => {
  if (filter.category && filterCategories?.[filter.category]) {
    const prop = filterCategories[filter.category].properties?.find(
      (p) => p.value === filter.property,
    );
    if (prop?.dataType) {
      return prop.dataType;
    }
  }
  if (filter.dataType) {
    return filter.dataType;
  }
  return "text";
};

// A shape-A filter is a range when the operator needs two slots — either because
// the dataType is inherently a range (numeric_range/date_range), because the
// operator is `between` on a date, or because date_relative/_past uses
// valueEnd as the unit slot alongside `value`.
const needsRangeSlots = (filter, dataType) => {
  if (dataType === "numeric_range" || dataType === "date_range") return true;
  if (dataType === "date_relative" || dataType === "date_relative_past") return true;
  if (dataType === "date" && filter.operator === "between") return true;
  return false;
};

/**
 * Determines if a filter row carries enough payload to be sent to the backend.
 * Per the canonical contract:
 *   - no-value operators always pass (operator carries semantics)
 *   - shape-B types need at least one appliedValue
 *   - shape-A range slots need both value and valueEnd
 *   - shape-A single types need value
 */
const isFilterComplete = (filter, dataType) => {
  if (NO_VALUE_OPERATORS.includes(filter.operator)) return true;

  if (SHAPE_B_DATA_TYPES.has(dataType)) {
    return Array.isArray(filter.appliedValues) && filter.appliedValues.length > 0;
  }

  if (needsRangeSlots(filter, dataType)) {
    return (
      filter.value !== "" && filter.value != null &&
      filter.valueEnd !== "" && filter.valueEnd != null
    );
  }

  // numeric, date (non-between), boolean
  return filter.value !== "" && filter.value != null;
};

/**
 * Builds the canonical wire payload for one filter row.
 * Branches by dataType + operator so each row carries exactly the fields its shape needs.
 * See docs/plans/2026-04-08-bk2907-advanced-filters-contract-refactor-plan.md
 */
const buildWireFilter = (filter, dataType) => {
  const base = {
    property: filter.property,
    operator: filter.operator,
    dataType,
  };

  if (NO_VALUE_OPERATORS.includes(filter.operator)) {
    return base;
  }

  if (SHAPE_B_DATA_TYPES.has(dataType)) {
    return {
      ...base,
      appliedValues: (filter.appliedValues || []).map((av) => ({
        value: av.value,
        label: av.label,
      })),
    };
  }

  if (needsRangeSlots(filter, dataType)) {
    return { ...base, value: filter.value, valueEnd: filter.valueEnd };
  }

  // numeric, date (non-between), boolean
  return { ...base, value: filter.value };
};

/**
 * Validates a list of UI-state filters before Apply/Save.
 * Returns { valid: true } or { valid: false, message } for the caller to surface.
 *
 * Used by useAdvancedFilterSearch.handleApply, search_form.handleApplyAdvancedFilters,
 * task_filters_form.handleApplyAdvancedFilters, and the SaveSearch flow — single
 * source of truth for "is this filter set complete enough to submit?".
 */
export const validateFiltersForApply = (filters, filterCategories) => {
  if (!Array.isArray(filters)) {
    return { valid: true };
  }

  for (const filter of filters) {
    if (!filter.property || !filter.operator) continue;

    const dataType = resolveDataType(filter, filterCategories);
    if (!isFilterComplete(filter, dataType)) {
      return {
        valid: false,
        message:
          "Please provide values for all filter conditions before applying.",
      };
    }
  }

  return { valid: true };
};

/**
 * Enriches filter objects with dataType from category config and emits the
 * canonical wire shape (BK-2907). Skips empty/incomplete filters.
 *
 * @param {Array} filters - Raw filter objects from UI state
 * @param {Object} filterCategories - Category config from getFilterCategoriesForPage
 * @returns {Array} API-ready filter objects in canonical wire shape
 */
export const buildApiFilters = (filters, filterCategories) => {
  if (!filters || !Array.isArray(filters)) {
    return [];
  }

  return filters
    .filter((f) => f.property && f.operator)
    .map((f) => ({ filter: f, dataType: resolveDataType(f, filterCategories) }))
    .filter(({ filter, dataType }) => isFilterComplete(filter, dataType))
    .map(({ filter, dataType }) => buildWireFilter(filter, dataType));
};
